/**
 * Permit Requirements Checklist — Phase 1B mutations + readiness.
 *
 * Per-job ledger of completion state for items resolved from
 * `requirements.ts`. Three statuses:
 *   - "pending"  (default — never persisted unless toggled away from)
 *   - "complete"
 *   - "n_a"
 *
 * Status transitions are reversible (operator can flip "complete" back
 * to "pending" if they over-clicked). The ledger only stores items the
 * operator has touched; items absent from the ledger render as pending.
 *
 * Readiness:
 *   readinessPct = complete / (applicable - n_a)
 *   When all applicable items are N/A → null (avoid divide-by-zero and
 *   avoid implying "100% ready" when nothing is actually verified).
 */
import type { Job } from "@/data/types";
import { getRequiredPermitItems, type RequirementItem } from "./requirements";

export type RequirementStatus = "pending" | "complete" | "n_a";

export interface RequirementChecklistEntry {
  key: string;
  status: RequirementStatus;
  /** Operator who last touched this item — same fallback chain as municipality log. */
  updatedBy: string;
  updatedAt: string;
  /** Stored URL for link-type requirement items. */
  linkUrl?: string;
}

export function getRequirementChecklist(job: Job | null | undefined): RequirementChecklistEntry[] {
  return job?.permitDetail?.requirementChecklist ?? [];
}

export function getRequirementStatus(
  job: Job | null | undefined,
  key: string,
): RequirementStatus {
  const entry = getRequirementChecklist(job).find((e) => e.key === key);
  return entry?.status ?? "pending";
}

/**
 * Set status for a single requirement key. Reversible. Returns updated Job.
 * Pure — caller wires into setJobs.
 */
export function setRequirementStatus(
  job: Job,
  key: string,
  status: RequirementStatus,
  updatedBy: string,
): Job {
  if (!job.permitDetail) return job;
  const existing = job.permitDetail.requirementChecklist ?? [];
  const without = existing.filter((e) => e.key !== key);
  const next: RequirementChecklistEntry[] = [
    ...without,
    { key, status, updatedBy, updatedAt: new Date().toISOString() },
  ];
  return {
    ...job,
    permitDetail: {
      ...job.permitDetail,
      requirementChecklist: next,
    },
  };
}

/**
 * Get the stored link URL for a link-type requirement item.
 */
export function getRequirementLinkUrl(
  job: Job | null | undefined,
  key: string,
): string {
  const entry = getRequirementChecklist(job).find((e) => e.key === key);
  return entry?.linkUrl ?? "";
}

/**
 * Set the link URL for a link-type requirement item. Pure — caller wires into setJobs.
 */
export function setRequirementLinkUrl(
  job: Job,
  key: string,
  linkUrl: string,
): Job {
  if (!job.permitDetail) return job;
  const existing = job.permitDetail.requirementChecklist ?? [];
  const existing_entry = existing.find((e) => e.key === key);
  const without = existing.filter((e) => e.key !== key);
  const next: RequirementChecklistEntry[] = [
    ...without,
    {
      key,
      status: existing_entry?.status ?? "pending",
      updatedBy: existing_entry?.updatedBy ?? "",
      updatedAt: existing_entry?.updatedAt ?? new Date().toISOString(),
      linkUrl,
    },
  ];
  return {
    ...job,
    permitDetail: {
      ...job.permitDetail,
      requirementChecklist: next,
    },
  };
}

export interface RequirementReadiness {
  applicable: number;
  complete: number;
  pending: number;
  notApplicable: number;
  /** complete / (applicable - n_a). Null when all applicable are N/A. */
  readinessPct: number | null;
  incompleteCount: number;
}

export function computeRequirementReadiness(
  job: Job | null | undefined,
  items: RequirementItem[],
): RequirementReadiness {
  const applicable = items.length;
  let complete = 0;
  let notApplicable = 0;
  for (const item of items) {
    const status = getRequirementStatus(job, item.key);
    if (status === "complete") complete++;
    else if (status === "n_a") notApplicable++;
  }
  const denom = applicable - notApplicable;
  const readinessPct = denom <= 0 ? null : Math.round((complete / denom) * 100);
  const pending = applicable - complete - notApplicable;
  return {
    applicable,
    complete,
    pending,
    notApplicable,
    readinessPct,
    incompleteCount: pending,
  };
}

/**
 * Convenience: resolve items + readiness for a job in one call.
 * `municipality` falls back to job.permitDetail.municipality.
 */
export function resolveJobRequirements(job: Job | null | undefined): {
  items: RequirementItem[];
  readiness: RequirementReadiness;
} {
  const municipality = job?.permitDetail?.municipality ?? null;
  const permitType = job?.permitDetail?.permitType ?? "";
  const hasSecondBathroom = job?.permitDetail?.hasSecondBathroom ?? false;
  const items = getRequiredPermitItems(municipality, { permitType, hasSecondBathroom });
  const readiness = computeRequirementReadiness(job, items);
  return { items, readiness };
}

/** Soft-warning copy for permit submission with incomplete requirements. */
export function buildIncompleteSubmitWarning(incompleteCount: number): string {
  return `${incompleteCount} permit requirement${incompleteCount === 1 ? "" : "s"} remain incomplete for this municipality. Submitting now may increase revision risk.`;
}
